'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import { LineInfoCircle } from '@qr-front/icons/line-info-circle'
import { LineLinkAlt01 } from '@qr-front/icons/line-link-alt-01'
import { LinePlus } from '@qr-front/icons/line-plus'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { Button, buttonCls } from '@qr-front/ui/components/Button'
import { cn } from '@qr-front/ui/utils'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { transformBackendResponseKey } from '@/config'
import { URL_MAX_LENGTH } from '@/constants'
import { useSyncFieldWithAtom } from '@/hooks'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { maxLength, validateContentForm } from '@/utils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../../components/GeneratorFormSection'
import { RhcField } from '../../components/RhcField'
import { urlSchema } from '../fieldSchemas'
import { ImageField } from '../ImageForm'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import { getAppInfoFormSchema, TAppInfoFields } from './AppInfoFormModel'
import { appButtonListAtom, ButtonsSection } from './ButtonsSection'
import { Amazon, Applestore, Miapp, Playstore } from './Icons'

export const APP_FORM_ID = 'app-form'
export const PLATFORM_FORM_ID = 'platform-form'

export const appNameAtom = atomWithReset<string>('')
export const appDeveloperAtom = atomWithReset<string | undefined>('')
export const appDescriptionAtom = atomWithReset<string | undefined>('')
export const appPlatformLinksAtom = atomWithReset<
	{ name: string; url: string }[]
>([])

export const resetAppForm = () => {
	getStore().set(appNameAtom, RESET)
	getStore().set(appDeveloperAtom, RESET)
	getStore().set(appDescriptionAtom, RESET)
	getStore().set(appButtonListAtom, RESET)
	getStore().set(appPlatformLinksAtom, RESET)
}

export const AppForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const form = useForm<TAppInfoFields>({
		defaultValues: {
			name: getStore().get(appNameAtom),
			developer: getStore().get(appDeveloperAtom),
			description: getStore().get(appDescriptionAtom),
			buttons: getStore().get(appButtonListAtom),
			platforms: getStore().get(appPlatformLinksAtom) || [],
		},
		mode: 'onTouched',
		resolver: yupResolver(getAppInfoFormSchema(t)),
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const {
		formState: { isValid },
		handleSubmit,
	} = form

	useSyncFormSectionValidity({ formId: APP_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				const translatedMessage = t(
					transformBackendResponseKey(message),
				)
				property = property.replace('appInfo.buttons.', 'buttons.')
				form.setError(property as Parameters<typeof form.setError>[0], {
					message: translatedMessage,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) =>
						handleSubmit(onValid, async (errors) => {
							onInvalid(errors)
						}),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	const { getValues, setFocus, setValue } = form

	const { watch } = form
	const buttonList = useAtomValue(appButtonListAtom)
	const setButtonList = useSetAtom(appButtonListAtom)

	const watchedButtons = watch('buttons')
	useEffect(() => {
		if (
			watchedButtons &&
			JSON.stringify(watchedButtons) !== JSON.stringify(buttonList)
		) {
			setButtonList(watchedButtons)
		}
		// TODO: Fix exhaustive deps warning
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchedButtons, setButtonList])

	useEffect(() => {
		const formButtons = getValues('buttons') || []
		if (formButtons.length === 0 && buttonList.length > 0) {
			setValue('buttons', buttonList, { shouldValidate: false })
		}
		// TODO: Fix exhaustive deps warning
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [buttonList.length, setValue, getValues])

	useSyncFieldWithAtom({
		atom: appPlatformLinksAtom,
		setFieldValue: (value) => setValue('platforms', value),
	})

	const nameField = (
		<RhcField
			type="text"
			form={form}
			path="name"
			required
			fieldName="name"
			atom={appNameAtom}
			label={t('generator__content_form_section__app__name__label')}
			placeholder={t(
				'generator__content_form_section__app__name__placeholder',
			)}
			maxLength={101}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const developerField = (
		<RhcField
			type="text"
			form={form}
			path="developer"
			fieldName="developer"
			atom={appDeveloperAtom}
			label={t(
				'generator__content_form_section__videos_information__developer__label',
			)}
			placeholder={t(
				'generator__content_form_section__app__developer__placeholder',
			)}
			maxLength={201}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const descriptionField = (
		<RhcField
			type="text"
			form={form}
			path="description"
			fieldName="description"
			atom={appDescriptionAtom}
			label={t(
				'generator__content_form_section__menu__products__description_label',
			)}
			placeholder={t(
				'generator__content_form_section__app__description__placeholder',
			)}
			maxLength={251}
			className="col-span-2"
		/>
	)

	const handleClickAddButton = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}
		setButtonList((prev) => [...prev, { title: '', url: '' }])
		queueMicrotask(() => {
			const buttons = getValues('buttons')
			if (buttons && buttons.length > 0) {
				setFocus(`buttons.${buttons.length - 1}.title`)
			}
		})
	}

	return (
		<fieldset className="-mt-8">
			<ImageField onValidChange={() => {}} />
			<div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-4">
				{nameField}
				{developerField}
				{descriptionField}
			</div>

			<FormProvider {...form}>
				<ButtonsSection />
			</FormProvider>

			<Button
				visual="soft"
				onPointerDown={handleClickAddButton}
				className="mt-6 tablet:mt-8 [&>svg]:size-4"
			>
				<LinePlus />
				{t(
					'generator__content_form_section__images_information__add_button',
				)}
			</Button>
		</fieldset>
	)
})

AppForm.displayName = 'AppForm'

export const appFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: APP_FORM_ID,
	title: t('generator__content_form_section__app__title'),
	description: t('generator__content_form_section__app__description'),
	icon: <LineInfoCircle />,
	Content: AppForm,
})

export const PlatformForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const platformSchema = yup.object({
		platforms: yup
			.array()
			.of(
				yup.object({
					name: yup.string().required(),
					url: urlSchema(t, { required: true }),
				}),
			)
			.test(
				'at-least-one-platform',
				t('ui__field_validation_errors__generic__required'),
				function (platforms) {
					return !platforms || platforms.length === 0 ? false : true
				},
			),
	})

	type TPlatformFormFields = yup.InferType<typeof platformSchema>

	const form = useForm<TPlatformFormFields>({
		defaultValues: {
			platforms: getStore().get(appPlatformLinksAtom) || [],
		},
		mode: 'onTouched',
		resolver: yupResolver(platformSchema),
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const {
		formState: { isValid, errors },
		handleSubmit,
		setValue,
		trigger,
		register,
	} = form

	useSyncFormSectionValidity({ formId: PLATFORM_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				const translatedMessage = t(
					transformBackendResponseKey(message),
				)
				property = property.replace('platformLinks.', 'platforms.')
				form.setError(property as Parameters<typeof form.setError>[0], {
					message: translatedMessage,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) =>
						handleSubmit(onValid, async (errors) => {
							onInvalid(errors)
						}),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	useSyncFieldWithAtom({
		atom: appPlatformLinksAtom,
		setFieldValue: (value) => setValue('platforms', value),
	})

	const { ref: platformsRef } = register('platforms')
	const errorRef = useCallback(
		(element: HTMLDivElement | null) => {
			if (element && platformsRef) {
				platformsRef(element)
			}
		},
		[platformsRef],
	)

	const PLATFORM_LIST = [
		{
			key: 'apple',
			name: t(
				'generator__content_form_section__app__platforms__app_store',
			),
			placeholder: t(
				'generator__content_form_section__app__platforms__app_store__label',
			),
			icon: Applestore,
		},
		{
			key: 'google',
			name: t(
				'generator__content_form_section__app__platforms__play_store',
			),
			placeholder: t(
				'generator__content_form_section__app__platforms__play_store__label',
			),
			icon: Playstore,
		},
		{
			key: 'amazon',
			name: t('generator__content_form_section__app__platforms__amazon'),
			placeholder: t(
				'generator__content_form_section__app__platforms__amazon__label',
			),
			icon: Amazon,
		},
		{
			key: 'xiaomi',
			name: t('generator__content_form_section__app__platforms__xiaomi'),
			placeholder: t(
				'generator__content_form_section__app__platforms__xiaomi__label',
			),
			icon: Miapp,
		},
	]
	const [platformLinks, setPlatformLinks] = useAtom(appPlatformLinksAtom)
	const platforms = PLATFORM_LIST.map((platform) => {
		const atomItem = platformLinks.find(
			(link) => link?.name === platform.key,
		)
		return {
			key: platform.key,
			name: platform.name,
			icon: platform.icon,
			placeholder: platform.placeholder,
			enabled: !!atomItem,
			url: atomItem?.url || '',
			linkIndex: atomItem ? platformLinks.indexOf(atomItem) : -1,
		}
	})

	const handleEnable = (platformKey: string) => {
		setPlatformLinks((prev) => {
			if (prev.some((link) => link.name === platformKey)) {
				return prev
			}
			return [...prev, { name: platformKey, url: '' }]
		})
		if (form.formState.isSubmitted) {
			queueMicrotask(() => trigger('platforms'))
		}
	}

	const handleDisable = (platformKey: string) => {
		setPlatformLinks((prev) =>
			prev.filter((link) => link.name !== platformKey),
		)
		if (form.formState.isSubmitted) {
			queueMicrotask(() => trigger('platforms'))
		}
	}

	const handleUrlChange = (platformKey: string, value: string) => {
		setPlatformLinks((prev) =>
			prev.map((link) =>
				link.name === platformKey ? { ...link, url: value } : link,
			),
		)
		if (form.formState.isSubmitted) {
			queueMicrotask(() => trigger('platforms'))
		}
	}

	return (
		<FormProvider {...form}>
			<fieldset>
				<div className="mb-8 flex gap-4">
					{platforms.map((p) => {
						const Icon = p.icon
						return (
							!p.enabled && (
								<button
									type="button"
									key={p.key}
									onClick={() => handleEnable(p.key)}
									className="transition-opacity hover:opacity-80"
								>
									<Icon height={60} width={60} />
								</button>
							)
						)
					})}
				</div>
				{errors.platforms && (
					<div
						ref={errorRef}
						id="platforms-error"
						tabIndex={-1}
						className="type-body-caption mt-1 text-left text-error"
					>
						{errors.platforms.message}
					</div>
				)}
				<div className="flex flex-col gap-2">
					{platforms.map((p) => {
						const Icon = p.icon
						return (
							p.enabled && (
								<div
									key={p.key}
									className="flex flex-col items-start justify-between gap-4 rounded-10 bg-light-grey-70 p-6 tablet:flex-row tablet:items-center"
								>
									<div className="flex items-center gap-2">
										<Icon className="h-10 w-10" />
										<span className="type-body-input-title">
											{p.name}
										</span>
									</div>
									<div className="flex w-full items-center gap-2 tablet:w-auto">
										<RhcField
											type="url"
											form={form}
											path={`platforms.${platformLinks.findIndex((link) => link.name === p.key)}.url`}
											value={p.url}
											setValue={(value) =>
												handleUrlChange(
													p.key,
													value || '',
												)
											}
											fieldName="platform_url"
											fieldGroup="platform_links"
											placeholder={p.placeholder}
											className="w-[calc(100%_-_56px)] tablet:w-[320px] desktop:w-[440px] [&>.text-error]:absolute [&>.text-error]:mt-0"
											onChangeTransform={(event) =>
												maxLength(URL_MAX_LENGTH + 1)(
													event,
												)
											}
										/>
										<button
											type="button"
											onClick={() => handleDisable(p.key)}
											className={cn(
												buttonCls({ visual: 'soft' }),
												'flex h-12 w-12 items-center justify-center',
											)}
										>
											<LineTrashAlt className="size-4" />
										</button>
									</div>
								</div>
							)
						)
					})}
				</div>
			</fieldset>
		</FormProvider>
	)
})
PlatformForm.displayName = 'PlatformForm'

export const platformFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: PLATFORM_FORM_ID,
	title: t('generator__content_form_section__platform__title'),
	description: t('generator__content_form_section__platform__description'),
	icon: <LineLinkAlt01 />,
	Content: PlatformForm,
})
